import type {
    BrandInterface,
    ColorInterface,
    FillInterface,
    GradientInterface,
    PaletteInterface,
    SpecialGradientInterface,
    SurfaceInterface,
    TextInterface,
} from '../types';

const Text: TextInterface = {
    x10: 'rgba(255, 255, 255, 0.9)',
    x20: 'rgba(255, 255, 255, 0.55)',
    x30: 'rgba(255, 255, 255, 0.3)',
    Black: 'rgba(0, 0, 0, 0.9)',
    White: 'rgba(255, 255, 255, 1)',
    Inverse: 'rgba(0, 0, 0, 0.9)',
};

const Fill: FillInterface = {
    x10: 'rgba(255, 255, 255, 0.9)',
    x20: 'rgba(255, 255, 255, 0.35)',
    x30: 'rgba(255, 255, 255, 0.25)',
    x40: 'rgba(255, 255, 255, 0.16)',
    x50: 'rgba(255, 255, 255, 0.12)',
    x55: 'rgba(255, 255, 255, 0.085)',
    x60: 'rgba(255, 255, 255, 0.05)',
    White: 'rgba(255, 255, 255, 1)',
    Inverse: 'rgba(0, 0, 0, 0.9)',
};

const Surface: SurfaceInterface = {
    x10: 'rgba(10, 12, 15, 1)',
    x20: 'rgba(19, 22, 26, 1)',
    x30: 'rgba(27, 30, 36, 1)',
    x40: 'rgba(51, 51, 51, 1)',
    Inverse: 'rgba(255, 255, 255, 1)',
    Overlay: 'rgba(0, 0, 0, 0.75)',
    Foreground: 'rgba(31, 31, 31, 1)',
};

const Sell: ColorInterface = {
    x10: 'rgba(255, 255, 255, 0.9)',
    x20: 'rgba(255, 255, 255, 0.08)',
    x30: 'rgba(255, 255, 255, 0.14)',
    x40: 'rgba(255, 255, 255, 0.45)',
    x50: 'rgba(255, 255, 255, 0.6)',
};

const Purple: ColorInterface = {
    x10: 'rgba(138, 92, 255, 1)',
    x20: 'rgba(138, 92, 255, 0.12)',
    x30: 'rgba(138, 92, 255, 0.16)',
    x40: 'rgba(166, 131, 255, 1)',
    x50: 'rgba(185, 157, 255, 1)',
};

const Blue: ColorInterface = {
    x10: 'rgba(47, 106, 245, 1)',
    x20: 'rgba(47, 106, 245, 0.12)',
    x30: 'rgba(47, 106, 245, 0.16)',
    x40: 'rgba(97, 142, 247, 1)',
    x50: 'rgba(130, 166, 249, 1)',
};

const Teal: ColorInterface = {
    x10: 'rgba(0, 203, 204, 1)',
    x20: 'rgba(0, 203, 204, 0.16)',
    x30: 'rgba(0, 203, 204, 0.32)',
    x40: 'rgba(61, 215, 216, 1)',
    x50: 'rgba(102, 224, 224, 1)',
};

const Green: ColorInterface = {
    x10: 'rgba(0, 221, 148, 1)',
    x20: 'rgba(0, 221, 148, 0.12)',
    x30: 'rgba(0, 221, 148, 0.18)',
    x40: 'rgba(61, 229, 174, 1)',
    x50: 'rgba(102, 235, 191, 1)',
};

const Yellow: ColorInterface = {
    x10: 'rgba(255, 225, 77, 1)',
    x20: 'rgba(255, 225, 77, 0.16)',
    x30: 'rgba(255, 225, 77, 0.32)',
    x40: 'rgba(255, 232, 120, 1)',
    x50: 'rgba(255, 237, 148, 1)',
};
const Orange: ColorInterface = {
    x10: 'rgba(255, 122, 0, 1)',
    x20: 'rgba(255, 122, 0, 0.12)',
    x30: 'rgba(255, 122, 0, 0.16)',
    x40: 'rgba(255, 154, 61, 1)',
    x50: 'rgba(255, 175, 102, 1)',
};

const Red: ColorInterface = {
    x10: 'rgba(255, 69, 77, 1)',
    x20: 'rgba(255, 69, 77, 0.14)',
    x30: 'rgba(255, 69, 77, 0.2)',
    x40: 'rgba(255, 114, 120, 1)',
    x50: 'rgba(255, 143, 148, 1)',
};

const Critical: ColorInterface = Red;

const Yes: ColorInterface = Green;
const No: ColorInterface = Red;

const Gradient: GradientInterface = {
    x10: 'radial-gradient(circle at 30% 120%, #00CBCC 0%, #00CBCC 5%, #2F6AF5 45%,#8A5CFF 60%,#FF4D4D 90%)',
};

const Special: SpecialGradientInterface = {
    Mint: '#00D991',
    Black: 'linear-gradient(135deg, #B2ADA1 0%, #A09C91 25%, #D1CEC7 50%, #8E8A81 75%, #B2ADA1 100%)',
    Gold: '#FFD400',
    Silver: '#B8CBD9',
    Bronze: '#E5AA95',
    Platinum: 'linear-gradient(135deg, #D9D9D9 0%, #AEAEAE 25%, #E8E8E8 50%, #828282 75%, #D9D9D9 100%)',
    Diamond:
        'linear-gradient(135deg, #95E5E5 0%, #A1A1E5 11.11%, #E5ACE5 22.22%, #F2F2DA 44.44%, #ACE5E5 55.56%, #7478A6 66.67%, #A1A1E5 77.78%, #D9D9C3 88.89%, #95E595 100%)',
    Brand: 'rgba(40, 204, 149, 1)',
};

const Brand: BrandInterface = {
    Primary: 'rgba(40, 204, 149, 1)',
    x20: 'rgba(229, 251, 244, 1)',
    x30: 'rgba(179, 244, 222, 1)',
    x40: 'rgba(128, 236, 200, 1)',
    x50: 'rgba(77, 228, 178, 1)',
    x60: 'rgba(0, 195, 130, 1)',
    x70: 'rgba(0, 152, 102, 1)',
    x80: 'rgba(0, 109, 73, 1)',
    Secondary: 'rgba(0, 65, 43, 1)',
    Tertiary: '#003221',
};

/** @deprecated Use `Palette` from `@/DesignSystem3/Palette` instead. */
export const Palette_DarkMode: PaletteInterface = {
    Text,
    Fill,
    Surface,
    Yes,
    No,
    Sell,
    Critical,
    Purple,
    Blue,
    Teal,
    Green,
    Yellow,
    Orange,
    Red,
    Gradient,
    Special,
    Brand,
};
